import type { ModelledAPIResponse } from "@certa/queries/types/globalSearch.types";
import type { SearchItem } from "../GlobalSearchWithSDO.schema";

export const STRONG_TAG_REGEX = /<strong>/g;

export const transformHighlights = (
  result: ModelledAPIResponse.SearchResult
): {
  text: string;
  matches: string[];
  shouldShow: boolean;
} => {
  if (
    result.type !== "file" &&
    result.type !== "field" &&
    result.type !== "workflow" &&
    result.type !== "object"
  ) {
    return { text: "", matches: [], shouldShow: false };
  }

  const cleanText = (text: string) => text.replace(STRONG_TAG_REGEX, "");

  switch (result.type) {
    case "field": {
      const text = cleanText(result.highlights?.[0] || "");
      return {
        text: result.body + " : " + text,
        matches: result.highlights || [],
        shouldShow: true
      };
    }

    case "workflow": {
      const text = cleanText(result.highlights?.[0] || "");
      return {
        text,
        matches: result.highlights || [],
        shouldShow: false
      };
    }

    case "object": {
      const text = cleanText(result.subDescription || "");
      return {
        text,
        matches: result.highlights || [],
        shouldShow: Boolean(result.fieldValue)
      };
    }

    case "file": {
      // For files, if a part of document was matched, we show the first match
      const text = cleanText(result.highlights?.[0] || "");
      return {
        text,
        matches: result.highlights || [],
        shouldShow: Boolean(
          result.document_data_match && result.highlights?.length > 0
        )
      };
    }

    default:
      return { text: "", matches: [], shouldShow: false };
  }
};

export const transformSearchResult = (
  result: ModelledAPIResponse.SearchResult
): SearchItem | null => {
  switch (result.type) {
    case "workflow":
      return {
        id: result.id,
        type: "workflow",
        title: result.name,
        description: result.kind?.name,
        logo: result.logo ?? undefined
      };

    case "field": {
      const kindName = result.workflow.kind?.name;
      const workflowName = result.workflow.name;
      const description = kindName;

      return {
        id: result.id,
        type: "field",
        title: workflowName,
        description,
        workflowId: result.workflow.id,
        stepId: result.step.id,
        fieldValue: result.answer
      };
    }

    case "file": {
      const kindName = result.workflow.kind?.name;
      const workflowName = result.workflow.name;
      const description =
        kindName && workflowName ? `${kindName}: ${workflowName}` : undefined;

      return {
        id: result.id,
        type: "file",
        title: result.fileName,
        description,
        workflowId: result.workflow.id,
        stepId: result.step.id
      };
    }

    case "object":
      return {
        id: result.id,
        type: "object",
        title: result.name,
        description: result.description,
        objectType: result.objectType.tag,
        objectId: result.objectInstance.uid
      };

    default:
      return null;
  }
};
